import { Key } from "@solid-primitives/keyed"
import { A, useSearchParams } from "@solidjs/router"
import { FaSolidChevronLeft, FaSolidChevronRight, FaSolidCircleDot, FaSolidDice, FaSolidFloppyDisk, FaSolidPause, FaSolidPencil, FaSolidPlay, FaSolidPlus, FaSolidRotateLeft, FaSolidRotateRight, FaSolidTrashCan } from "solid-icons/fa"
import { batch, createEffect, createMemo, createSignal, For, on, onMount, untrack, type Accessor, Suspense } from "solid-js"
import { createMutable } from "solid-js/store"
import toast from "solid-toast"
import { Vector3, type Object3D } from "three"
import type { Nullable } from "vite-node"
import { PlayerColorPicker } from "../../components/ArmyPainter/PlayerColorPicker"
import PlayerColorsBar from "../../components/ArmyPainter/PlayerColorsBar"
import EngineContext from "../../components/EngineContext"
import EmSmallTextMuted from "../../components/Util/EmSmallTextMuted"
import TextMuted from "../../components/Util/TextMuted"
import bindViewCtxToWindowV2 from "../../lib/bindViewCtxToWindowV2"
import bindEngineToComponentV2 from "../../lib/canvas/bindEngineToComponentV2"
import type { ArmyPaint } from "../../lib/core/ArmyPainter/ArmyPaint.type"
import { BiomeTypeId } from "../../lib/core/ArmyPainter/BiomeTypeId.enum"
import setDefaultMapControlArmyPainterAngle from "../../lib/core/ArmyPainter/setDefaultMapControlArmyPainterAngle"
import updateArmyPainterBiome from "../../lib/core/ArmyPainter/updateArmyPainterBiome"
import createEngineForArmyPainter from "../../lib/core/engine/createEngineForArmyPainter"
import type { Engine } from "../../lib/core/engine/Engine.type"
import setEngineGridWidthHeight from "../../lib/core/engine/setEngineGridWidthHeight"
import type { EntityId } from "../../lib/core/entity/EntityId.type"
import { MediumTankEntityTypeMeta } from "../../lib/core/entity/EntityType/MediumTank"
import entityTypeMetaList from "../../lib/core/entity/entityTypeMetaList.generated"
import { EntityTypeGroupId } from "../../lib/core/EntityTypeGroupId.enum"
import findById from "../../lib/core/findById"
import type { PlayerColor } from "../../lib/core/player/PlayerColor.type"
import type { PlayerColorKey } from "../../lib/core/player/PlayerColorKey.type"
import randomColorInt from "../../lib/core/player/randomColorInt"
import tmpRngWith from "../../lib/core/rng/tmpRngWith"
import { random } from "../../lib/core/util/math/Math"
import parseIntOrNull from "../../lib/core/util/math/parseIntOrNull"
import { randomId } from "../../lib/core/util/rand"
import preventDefault from "../../lib/dom/event/preventDefault"
import registerTouchEventsV2 from "../../lib/dom/event/registerTouchEventsV2"
import { isPlainObjDeeplyEqual } from "../../lib/isPlainObjDeeplyEqual"
import Button from "../../lib/jsx/Button"
import alwaysFalse from "../../lib/ldsh/alwaysFalse"
import leadingDebounce from "../../lib/ldsh/leadingDebounce"
import onVisualViewportEvent from "../../lib/onVisualViewportEvent"
import onWindowEvent from "../../lib/onWindowEvent"
import { VERTICES_PER_TILE } from "../../lib/three/consts"
import createMainCamera from "../../lib/three/fn/createMainCamera"
import createMountedMainCanvasElement from "../../lib/three/fn/createMountedMainCanvasElement"
import createMountedMainRenderer from "../../lib/three/fn/createMountedMainRenderer"
import findFirstThreeObject3D from "../../lib/three/fn/findFirstThreeObject3D"
import { getEngineThreeControls } from "../../lib/three/fn/getSetEngineThreeControls"
import { setEngineThreeMainCamera } from "../../lib/three/fn/getSetEngineThreeMainCamera"
import { setEngineThreeMainRenderer } from "../../lib/three/fn/getSetEngineThreeMainRenderer"
import { setEngineToast } from "../../lib/three/fn/getSetEngineToast"
import useMainScene from "../../lib/three/fn/useMainScene"
import tweenVector3To from "../../lib/three/tween/tweenVector3To"
import createSetEngineAuthPlayerIdFromAuthProfileSignalEffect from "../../rx/effect/createSetEngineAuthPlayerIdFromAuthProfileSignalEffect"
import { getAuthArmyPaint, setAuthArmyPaint } from "../../rx/shared/ArmyPainter/AuthArmyPaintSignal"
import createRandomArmyPaint from "../../rx/shared/ArmyPainter/createRandomArmyPaint"
import deleteArmyPaintToDisk from "../../rx/shared/ArmyPainter/deleteArmyPaintToDisk"
import saveArmyPaintToDisk from "../../rx/shared/ArmyPainter/saveArmyPaintToDisk"
import { getAuthProfile } from "../../rx/shared/profile/createAuthProfileSignal"
import createArmyPaintCollectionResource from "../../rx/resource/createArmyPaintCollectionResource"
import {
  getStateMapDiskFromLocalStorage,
} from "../../lib/core/state/state_map_disk.helpers"
import { StateMapDisk } from "../../lib/core/state/state_map_disk.enum"
import DiskSelect from "../../components/DiskSelect"

const ARMY_PAINT_DISK_LS_KEY = 'army_paint_disk'

const ArmyPainterIndexPage = () => {

  const [searchParams, setSearchParams] = useSearchParams<Record<'army_id' | 'etype_id', string>>()

  const armyPaintIdFromUrl = createMemo<Nullable<number>>((): Nullable<number> => parseIntOrNull(searchParams.army_id))

  const [selectedDisk, setSelectedDisk] = createSignal<StateMapDisk>(
    getStateMapDiskFromLocalStorage(ARMY_PAINT_DISK_LS_KEY, () => StateMapDisk.IndexedDB)
  )

  const [getArmyPaint, setArmyPaint] = createSignal<ArmyPaint>(
    getAuthArmyPaint() || createRandomArmyPaint(randomId([], tmpRngWith(random()))),
    { equals: isPlainObjDeeplyEqual }
  )

  const [isArmyPaintListOpen, setIsArmyPaintListOpen] = createSignal<boolean>(false)

  const armyPaintCollectionResource = createArmyPaintCollectionResource(() => ({
    disk: selectedDisk()
  }))

  createEffect(on(armyPaintIdFromUrl, (ap_id) => {
    const list = armyPaintCollectionResource[0]()
    if (list && ap_id) {
        const ap = findById(list, ap_id)
        if (ap) {
            setArmyPaint(ap)
            setIsArmyPaintListOpen(false)
        } else {
            setIsArmyPaintListOpen(true)
        }
    }
  }))

  const [getBiomeId] = createSignal<BiomeTypeId>(BiomeTypeId.Snow)

  const paintableEntityTypeMetaList = entityTypeMetaList.filter(etm => etm.group_id === EntityTypeGroupId.Property || etm.group_id === EntityTypeGroupId.Unit)

  const selectedEntityTypeMeta = createMemo(() => {
    const etype_id = parseIntOrNull(searchParams.etype_id)
    return paintableEntityTypeMetaList.find((meta) => meta.id === etype_id) || MediumTankEntityTypeMeta
  })
  const nextEntityTypeMeta = createMemo(() => {
    const currentMetaId = selectedEntityTypeMeta().id
    const currentIndex = paintableEntityTypeMetaList.findIndex((meta) => meta.id === currentMetaId)
    return paintableEntityTypeMetaList[currentIndex + 1] || paintableEntityTypeMetaList[0]
  })
  const prevEntityTypeMeta = createMemo(() => {
    const currentMetaId = selectedEntityTypeMeta().id
    const currentIndex = paintableEntityTypeMetaList.findIndex((meta) => meta.id === currentMetaId)
    return paintableEntityTypeMetaList.at(currentIndex - 1)!
  })

  const getNewArmyPaintId = (): number => randomId(armyPaintCollectionResource[0]() || [], tmpRngWith(random()))

  const isSelectedDefault = createMemo(() => {
    return getArmyPaint().id === getAuthArmyPaint()?.id
  })

  const engine: Engine = createMutable<Engine>(createEngineForArmyPainter())
  setEngineGridWidthHeight(engine, 5, 5)

  setEngineToast(engine, toast)

  const canvasElem = createMountedMainCanvasElement()
  const renderer = createMountedMainRenderer(canvasElem)
  setEngineThreeMainRenderer(engine, renderer)
  const camera = createMainCamera(canvasElem)
  setEngineThreeMainCamera(engine, camera)

  bindViewCtxToWindowV2(engine, canvasElem)
  bindEngineToComponentV2(engine, canvasElem)

  createEffect(() => {
    const ap = getArmyPaint()
    batch(() => {
      const p0 = engine.state.players[0]
      if (p0) {
        p0.color1 = ap.color1
        p0.color2 = ap.color2
      }
    })
  })

  const ROTATE_LEFT_SPEED = 0.5
  const ROTATE_RIGHT_SPEED = -0.5

  const [getRotatePaused, setRotatePaused] = createSignal(true)
  const [getRotateSpeed, setRotateSpeed] = createSignal(ROTATE_LEFT_SPEED)
  const [getUnitId, setUnitId] = createSignal<EntityId>(0)

  onMount(() => {
    createSetEngineAuthPlayerIdFromAuthProfileSignalEffect(engine, getAuthProfile)
    const scene = useMainScene(engine)
    const mc = getEngineThreeControls(engine)
    const offset = VERTICES_PER_TILE * -1
    mc.target.set(offset, 0, offset)

    let firstTween = true

    const defaultValue = VERTICES_PER_TILE * 2
    const defaultPosition = new Vector3(defaultValue, VERTICES_PER_TILE, defaultValue)

    createEffect(() => {
      const entId = getUnitId()
      const unitEntity = findById(engine.state.ents, entId)
      setTimeout(() => {
        const unitObj = findFirstThreeObject3D(scene, (obj: Object3D) => {
          return obj.userData.id === entId
        })
        const targetPosition = unitObj?.position || defaultPosition
        if (firstTween) {
          firstTween = false
          mc.target.copy(targetPosition)
        } else {
          tweenVector3To(mc.target, targetPosition, 1500)
        }
        setDefaultMapControlArmyPainterAngle(mc, unitEntity?.etype_id)
      }, 100)
    })

    mc.enableDamping = true
    mc.dampingFactor = 0.06

    createEffect(() => {
      const paused = getRotatePaused()
      const value = paused ? 0 : getRotateSpeed()
      mc.autoRotate = !paused
      mc.autoRotateSpeed = value
    })

    createEffect(() => {
      const biomeId = getBiomeId()
      const entityTypeId = selectedEntityTypeMeta().id
      untrack(() => {
        updateArmyPainterBiome(engine, biomeId, entityTypeId, setUnitId)
      })
    })

    setTimeout(() => {
      const biomeId = getBiomeId()
      const entityTypeId = selectedEntityTypeMeta().id
      untrack(() => {
        updateArmyPainterBiome(engine, biomeId, entityTypeId, setUnitId)
      })
    }, 1)
  })

  function updatePlayerColor(key: PlayerColorKey, newColor: PlayerColor) {
    const player = engine.state.players[0]!
    player[key] = newColor
  }

  const colors = [
    { dname: 'Primary', key: 'color1' },
    { dname: 'Secondary', key: 'color2' },
  ] as const

  const originalArmyPaint = createMemo(() => {
    const list = armyPaintCollectionResource[0]()
    return findById(list || [], getArmyPaint().id)
  })

  const isDirty = createMemo(() => {
    return !isPlainObjDeeplyEqual(originalArmyPaint(), getArmyPaint())
  })

  function navigateToList() {
    setSearchParams({ ...searchParams, army_id: void 0 })
    setIsArmyPaintListOpen(true)
  }

  // eslint-disable-next-line solid/reactivity
  onWindowEvent('keydown', (event) => {
    if (event.code === 'Escape') {
      if (!isArmyPaintListOpen()) {
        if (isDirty()) {
          toast.error('Save or Reset changes to Army before leaving?')
        } else {
          navigateToList()
        }
      }
      preventDefault(event)
      return false
    }

    if (event.code === 'KeyS' && (event.ctrlKey || event.metaKey)) {
      saveArmyPaintToDisk(getArmyPaint())
      return false
    }

    if (event.code === 'Comma') {
      gotoPrevEntityType()
      return false
    }
    if (event.code === 'Period') {
      gotoNextEntityType()
      return false
    }
    if (event.code === 'ArrowLeft') {
      setRotateSpeed(ROTATE_LEFT_SPEED)
      setRotatePaused(!getRotatePaused())
      return false
    }
    if (event.code === 'ArrowRight') {
      setRotateSpeed(ROTATE_RIGHT_SPEED)
      setRotatePaused(!getRotatePaused())
      return false
    }
  })

  function gotoPrevEntityType() {
    setSearchParams({ ...searchParams, etype_id: prevEntityTypeMeta().id })
  }

  function gotoNextEntityType() {
    setSearchParams({ ...searchParams, etype_id: nextEntityTypeMeta().id })
  }

  const [getInset, setInset] = createSignal('')

  function updateInset() {
    const probe = document.getElementById('cmdp')
    const computed = window.getComputedStyle(probe as HTMLElement)
    const safeAreaInsetBottom = computed.height
    const bottomInset = window.innerHeight - document.documentElement.clientHeight
    setInset(`window.innerHeight: ${window.innerHeight}\ndocument.documentElement.clientHeight: ${document.documentElement.clientHeight}\nbottomInset: ${bottomInset}\nsafe-area-inset-bottom: ${safeAreaInsetBottom}`)
  }

  onWindowEvent('resize', updateInset)
  onVisualViewportEvent('resize', updateInset)
  onMount(updateInset)

  return <EngineContext.Provider value={engine}>
    <div id="gpc">
      <div id="gvp" ref={(ref: HTMLElement) => {
        if (ref) {
          registerTouchEventsV2(engine, ref)
        }
      }}>
        {canvasElem}
      </div>
      <div id="cmdp">
        <div class="d-flex flex-wrap justify-content-between">
          <pre class="small">{getInset()}</pre>
          <Button class="btn btn-outline-secondary btn-sm m-1 text-nowrap"
            onClick={() => setRotatePaused(!getRotatePaused())}
          >
            Spin {getRotatePaused() ? <FaSolidPause /> : <FaSolidPlay />}
          </Button>
          <Button class="btn btn-outline-secondary btn-sm m-1 text-nowrap"
            classList={{ 'text-muted': getRotatePaused() }}
            onClick={() => setRotateSpeed(getRotateSpeed() * -1)}
          >
            {getRotateSpeed() > 0 ? <FaSolidRotateLeft /> : <FaSolidRotateRight />}
          </Button>
          <Button class="btn btn-outline-secondary btn-sm m-1 text-nowrap"
            onClick={gotoPrevEntityType}
          >
            <FaSolidChevronLeft /> {prevEntityTypeMeta().dname}
          </Button>
          <Button class="btn btn-outline-secondary btn-sm m-1 text-nowrap"
            disabled={true}
            onClick={alwaysFalse}
          >
            {selectedEntityTypeMeta().dname}
          </Button>
          <Button class="btn btn-outline-secondary btn-sm m-1 text-nowrap"
            onClick={gotoNextEntityType}
          >
            {nextEntityTypeMeta().dname} <FaSolidChevronRight />
          </Button>
        </div>
      </div>
    </div>
    <div id="apsw">
      <div class="ap-list m-2 p-2 w-16-rem" classList={{ 'd-none': !isArmyPaintListOpen() }}>
        <h2>Army
          <Button class="btn btn-sm float-end btn-success"
            onClick={() => {
              const newAp = createRandomArmyPaint(getNewArmyPaintId())
              newAp.disk = selectedDisk()
              setArmyPaint(newAp)
              setIsArmyPaintListOpen(false)
            }}>
            <FaSolidPlus /> New
          </Button>
        </h2>

        <DiskSelect
            value={selectedDisk()}
            onChange={setSelectedDisk}
            localStorageKey={ARMY_PAINT_DISK_LS_KEY}
        />

        <ul class="list-unstyled">
          <Suspense fallback={<div>Loading...</div>}>
            <Key each={armyPaintCollectionResource[0]()} by="id"
                fallback={EmSmallTextMuted('click new to create your first army')}
                children={(ap: Accessor<ArmyPaint>) => {
                return <li class="d-flex justify-content-between m-1 p-1">
                    <Button class="btn btn-sm hover-target"
                    onClick={() => {
                        const armyPaint = ap()
                        const isSelected = getAuthArmyPaint()?.id === armyPaint.id
                        setAuthArmyPaint(isSelected ? null : armyPaint)
                        setArmyPaint(armyPaint)
                    }}
                    ariaLabel={getAuthArmyPaint()?.id === ap().id ? 'Selected Default Army' : 'Click to set selected'}
                    >
                    <FaSolidCircleDot classList={{
                        'text-muted opacity-0 d-on-hover': ap().id !== getAuthArmyPaint()?.id,
                    }} />
                    </Button>
                    <A href={`/army-painter/?army_id=${ap().id}&etype_id=${selectedEntityTypeMeta().id}`}
                    class="btn w-100 text-start mx-2 hover-target"
                    classList={{ 'border border-1': ap().id === getAuthArmyPaint()?.id }}
                    >
                    <PlayerColorsBar value={ap()} width={16} height={16} />{' '}
                    {ap().dname || TextMuted('Unnamed Army')}{' '}
                    <FaSolidPencil class="d-none d-on-hover" />
                    </A>
                    <Button class="btn btn-outline-secondary btn-sm"
                    onClick={
                      // eslint-disable-next-line solid/reactivity
                      async () => {
                        const armyPaint = ap()
                        const isSelected = getArmyPaint().id === armyPaint.id
                        await deleteArmyPaintToDisk(armyPaint)
                        armyPaintCollectionResource[1].refetch()
                        if (isSelected) {
                            setArmyPaint(createRandomArmyPaint(getNewArmyPaintId()))
                        }
                      }
                    }
                    >
                    <FaSolidTrashCan />
                    </Button>
                </li>
                }} />
          </Suspense>
        </ul>
      </div>
      <div class="ap-edit" classList={{ 'd-none': isArmyPaintListOpen() }}>
        <div class="m-2 p-2 form-floating">
          <input
            id="apEditName"
            class="form-control"
            type="text"
            value={getArmyPaint().dname || ''}
            onInput={
              // eslint-disable-next-line solid/reactivity
              leadingDebounce((e) => {
                const value = (e.target as HTMLInputElement).value
                setArmyPaint({
                  ...getArmyPaint(),
                  dname: value,
                })
              }, 100)}
            placeholder="Army Name"
          />
          <label for="apEditName">Army Name</label>
        </div>
        <div class="m-2 p-2">
          <label>
            <input
              type="checkbox"
              checked={isSelectedDefault()}
              onChange={(e) => {
                const ap = getArmyPaint()
                if (e.currentTarget.checked) {
                  setAuthArmyPaint(ap)
                } else {
                  setAuthArmyPaint(null)
                }
              }}
            /> <em class="small ps-1" classList={{ 'text-muted': !isSelectedDefault() }}>My Default Army</em>
          </label>
        </div>
        <For each={colors} children={({ dname, key }) => {
          const inputId = `apcp${key}`
          return (
            <div class="d-flex m-2 p-2 align-items-center">
              <label class="me-2 align-self-center" for={inputId}>{dname}:</label>
              <div class="ms-auto d-flex align-items-center">
                <Button class="btn btn-outline-secondary btn-sm align-self-center" onClick={() => {
                  setArmyPaint({
                    ...getArmyPaint(),
                    [key]: randomColorInt(tmpRngWith(random())),
                  })
                }}>
                  <FaSolidDice />
                </Button>
                <PlayerColorPicker
                  id={inputId}
                  class="ms-1 align-self-center"
                  key={key}
                  value={getArmyPaint()[key]}
                  onChange={(color: number) => {
                    setArmyPaint({
                      ...getArmyPaint(),
                      [key]: color,
                    })
                  }}
                  onInput={(color: number) => {
                    updatePlayerColor(key, color)
                  }}
                />
              </div>
            </div>
          )
        }} />
        <div class="m-2 p-2">
          <div class="btn-group-vertical w-100">
            <Button class="btn btn-secondary btn-sm" onClick={() => {
              const rng = tmpRngWith(random())
              setArmyPaint({
                ...getArmyPaint(),
                color1: randomColorInt(rng),
                color2: randomColorInt(rng),
              })
            }}>
              <FaSolidDice /> Randomize
            </Button>
            <Button class="btn btn-secondary btn-sm" disabled={!isDirty()} onClick={
              // eslint-disable-next-line solid/reactivity
              async () => {
                await saveArmyPaintToDisk(getArmyPaint())
                armyPaintCollectionResource[1].refetch()
              }}>
              <FaSolidFloppyDisk /> Save
            </Button>
            <Button class="btn btn-secondary btn-sm" disabled={!(originalArmyPaint() && isDirty())} onClick={() => {
              const original = originalArmyPaint()
              if (original) {
                setArmyPaint(original)
              }
            }}>
              <FaSolidTrashCan /> Reset
            </Button>
            <Button class="btn btn-secondary btn-sm" onClick={navigateToList}>
              <FaSolidChevronLeft /> Back
            </Button>
          </div>
        </div>
      </div>
    </div>
  </EngineContext.Provider>
}

export default ArmyPainterIndexPage
